/**
 * Transform panel - globe status, editor selection. Triggers child panels: VisualPositionEditor, MultiGCPEditor.
 * Init is triggered by AssetViewer via initWithAssetViewer when asset is ready.
 */
import { removeAssetFromGlobe, saveActiveEditor, submitAssetGlobeStatusDisplay } from "../construkted_ajax";
import { GlobeDisplayStatus } from "../global";
import type AssetViewer from "../AssetViewer";
import { AssetGeoLocation, ConstruktedTransformEditors } from "../types/common";
import { BasePanel } from "./BasePanel";
import type { PanelContainer } from "./BasePanel";

type RemoveCallback = () => void;

export class TransformPanel extends BasePanel {
    private _container: ReturnType<typeof window.jQuery> | null = null;

    private _jqLatitudeLabel!: ReturnType<typeof window.jQuery>;
    private _jqLongitudeLabel!: ReturnType<typeof window.jQuery>;
    private _jqElevationLabel!: ReturnType<typeof window.jQuery>;
    private _jqVisualPositionEditorContainer!: ReturnType<typeof window.jQuery>;
    private _jqMultiGCPsEditorContainer!: ReturnType<typeof window.jQuery>;
    private _jqTransformContainer!: ReturnType<typeof window.jQuery>;
    private _jqGlobeStatusDisplayContainer!: ReturnType<typeof window.jQuery>;
    private _jqPredefinedGeoreferenceCheckboxContainer!: ReturnType<typeof window.jQuery>;
    private _jqPredefinedGeoreferenceCheckbox!: ReturnType<typeof window.jQuery>;
    private _jqVisualGeolocationEditorCheckbox!: ReturnType<typeof window.jQuery>;
    private _jqMultiGCPLocationEditorCheckbox!: ReturnType<typeof window.jQuery>;
    private _jqPredefinedGeoreferenceEditButton!: ReturnType<typeof window.jQuery>;
    private _jqVisualGeolocationEditorEditButton!: ReturnType<typeof window.jQuery>;
    private _jqMultiGCPLocationEditorEditButton!: ReturnType<typeof window.jQuery>;
    private _jqSubmitToGlobeButton!: ReturnType<typeof window.jQuery>;
    private _jqRemoveFromGlobeButton!: ReturnType<typeof window.jQuery>;
    private _jqAssetNotGeoreferencedDescription!: ReturnType<typeof window.jQuery>;
    private _jqAssetGeoreferencedDescription!: ReturnType<typeof window.jQuery>;
    private _jqAssetSubmittedDescription!: ReturnType<typeof window.jQuery>;
    private _jqAssetApprovedDescription!: ReturnType<typeof window.jQuery>;

    private _removeVisualPositionEditorDeactivated: RemoveCallback | undefined;

    getId(): string {
        return "transform";
    }

    getContainer(): PanelContainer {
        if (!this._container) {
            this._container = window.jQuery("#construkted-popup-transform");
        }

        return this._container;
    }

    /** Called by AssetViewer when asset is ready. Sets up all transform popup logic. */
    initWithAssetViewer(assetViewer: AssetViewer): void {
        const jQuery = window.jQuery;

        this._jqLatitudeLabel = jQuery("#geolocation-latitude-label");
        this._jqLongitudeLabel = jQuery("#geolocation-longitude-label");
        this._jqElevationLabel = jQuery("#geolocation-elevation-label");
        this._jqTransformContainer = jQuery("#transform-container");
        this._jqGlobeStatusDisplayContainer = jQuery("#globe-status-display");
        this._jqVisualPositionEditorContainer = jQuery("#visual");
        this._jqMultiGCPsEditorContainer = jQuery("#multi-gcps");
        this._jqPredefinedGeoreferenceCheckboxContainer = jQuery("#predefined-georeference-checkbox-container");
        this._jqPredefinedGeoreferenceCheckbox = jQuery("#predefined-georeference-checkbox");
        this._jqVisualGeolocationEditorCheckbox = jQuery("#visual-geolocation-editor-checkbox");
        this._jqMultiGCPLocationEditorCheckbox = jQuery("#multi-gcp-location-editor-checkbox");
        this._jqPredefinedGeoreferenceEditButton = jQuery("#predefined-georeference-edit");
        this._jqVisualGeolocationEditorEditButton = jQuery("#visual-geolocation-editor-edit");
        this._jqMultiGCPLocationEditorEditButton = jQuery("#multi-gcp-location-editor-edit");
        this._jqSubmitToGlobeButton = jQuery("#submit-to-globe-button");
        this._jqRemoveFromGlobeButton = jQuery("#remove-from-globe-button");
        this._jqAssetNotGeoreferencedDescription = jQuery("#asset-not-georeferenced-description");
        this._jqAssetGeoreferencedDescription = jQuery("#asset-georeferenced-description");
        this._jqAssetSubmittedDescription = jQuery("#asset-submitted-description");
        this._jqAssetApprovedDescription = jQuery("#asset-approved-description");

        if (!window.CONSTRUKTED_AJAX.originally_georeferenced) {
            this._jqPredefinedGeoreferenceCheckboxContainer.hide();
        }

        this.updateTransformPopup();

        const CONSTRUKTED_AJAX = window.CONSTRUKTED_AJAX;

        if (!CONSTRUKTED_AJAX.is_owner) {
            this._jqVisualGeolocationEditorEditButton.prop("disabled", true);
            this._jqMultiGCPLocationEditorEditButton.prop("disabled", true);
        }

        const onChangeEditorCheckbox = async () => {
            let activeEditor = ConstruktedTransformEditors.VisualPositionEditor;

            if (this._jqMultiGCPLocationEditorCheckbox.prop("checked")) {
                activeEditor = ConstruktedTransformEditors.MultiGCPsEditor;
            } else if (this._jqPredefinedGeoreferenceCheckbox.prop("checked")) {
                activeEditor = ConstruktedTransformEditors.PredefinedGeoreference;
            }

            if (CONSTRUKTED_AJAX.active_editor === activeEditor) {
                return;
            }

            const asset = assetViewer.masterAsset;

            await saveActiveEditor(activeEditor, () => {
                CONSTRUKTED_AJAX.active_editor = activeEditor;
                this._updateGeolocationLabels();

                let geolocation: AssetGeoLocation | undefined;

                if (
                    activeEditor === ConstruktedTransformEditors.PredefinedGeoreference &&
                    CONSTRUKTED_AJAX.asset_geo_location_by_pre
                ) {
                    geolocation = CONSTRUKTED_AJAX.asset_geo_location_by_pre;
                }

                if (
                    activeEditor === ConstruktedTransformEditors.VisualPositionEditor &&
                    CONSTRUKTED_AJAX.asset_geo_location
                ) {
                    geolocation = CONSTRUKTED_AJAX.asset_geo_location;
                }

                if (
                    activeEditor === ConstruktedTransformEditors.MultiGCPsEditor &&
                    CONSTRUKTED_AJAX.asset_geo_location_by_gcps
                ) {
                    geolocation = CONSTRUKTED_AJAX.asset_geo_location_by_gcps;
                }

                if (geolocation) {
                    asset.georeference(geolocation);
                } else {
                    asset.removeGeoreference();
                }

                assetViewer.cesiumViewer.camera.flyToBoundingSphere(asset.tileset.boundingSphere);

                if (activeEditor === ConstruktedTransformEditors.VisualPositionEditor) {
                    assetViewer.showHideSceneBackground(CONSTRUKTED_AJAX.terrain_imagery_enabled);
                } else {
                    assetViewer.showHideSceneBackground(true);
                }

                this._setVisibilityGeolocation();
            });
        };

        this._jqPredefinedGeoreferenceCheckbox.change(() => onChangeEditorCheckbox());
        this._jqVisualGeolocationEditorCheckbox.change(() => onChangeEditorCheckbox());
        this._jqMultiGCPLocationEditorCheckbox.change(() => onChangeEditorCheckbox());

        const getVisualPositionEditorPanel = () =>
            window.panelManager?.getPanel("visual-position-editor") as
                | {
                      initWithVisualPositionEditor: (e: unknown) => void;
                      displayGeolocation: (g: {
                          latitude: number;
                          longitude: number;
                          height: number;
                          heading: number;
                          pitch: number;
                          roll: number;
                      }) => void;
                      displayHeight: (h: number) => void;
                      setPredefinedGeoLocationMode: (v: boolean) => void;
                      _showHideTerrainImageryRelativeControls: (v: boolean) => void;
                  }
                | undefined;

        this._jqPredefinedGeoreferenceEditButton.click(() => {
            const visualPositionEditor = assetViewer.visualPositionEditor;

            if (!visualPositionEditor) {
                return;
            }

            const asset = window.Construkted.assetViewer.masterAsset;
            const panel = getVisualPositionEditorPanel();
            const geolocation =
                CONSTRUKTED_AJAX.asset_geo_location_by_pre ||
                (asset.originallyGeoreferencedAtECEF ? asset.originalGeolocation() : undefined);

            visualPositionEditor.activate({
                activateTransformEditor: false,
                geolocation: geolocation,
                terrainImageryEnabled: true
            });
            panel?.initWithVisualPositionEditor(visualPositionEditor);
            panel?.displayGeolocation(asset.calculateGeolocation());

            if (asset.hasValidGeoRef()) {
                panel?.displayHeight(asset.heightOfGeoRef());
            }

            panel?.setPredefinedGeoLocationMode(true);
            panel?._showHideTerrainImageryRelativeControls(true);

            if (this._removeVisualPositionEditorDeactivated) {
                this._removeVisualPositionEditorDeactivated();
            }

            this._removeVisualPositionEditorDeactivated = visualPositionEditor.deactivated.addEventListener(() => {
                this.showFirstPage();
                this.updateTransformPopup();
            });
            window.panelManager?.openPanelSync("visual-position-editor", { parentPanelId: "transform" });
        });

        this._jqVisualGeolocationEditorEditButton.click(() => {
            const visualPositionEditor = assetViewer.visualPositionEditor;

            if (!visualPositionEditor) {
                return;
            }

            const panel = getVisualPositionEditorPanel();
            const asset = assetViewer.masterAsset;

            visualPositionEditor.activate({
                activateTransformEditor: true,
                geolocation: CONSTRUKTED_AJAX.asset_geo_location,
                terrainImageryEnabled: CONSTRUKTED_AJAX.terrain_imagery_enabled
            });
            panel?.initWithVisualPositionEditor(visualPositionEditor);
            panel?.displayGeolocation(asset.calculateGeolocation());

            if (asset.hasValidGeoRef()) {
                panel?.displayHeight(asset.heightOfGeoRef());
            }

            panel?.setPredefinedGeoLocationMode(false);

            if (this._removeVisualPositionEditorDeactivated) {
                this._removeVisualPositionEditorDeactivated();
            }

            this._removeVisualPositionEditorDeactivated = visualPositionEditor.deactivated.addEventListener(() => {
                this.showFirstPage();
                this.updateTransformPopup();
            });
            window.panelManager?.openPanelSync("visual-position-editor", { parentPanelId: "transform" });
        });

        this._jqMultiGCPLocationEditorEditButton.click(() => {
            const gcpManager = assetViewer.gcpManager;

            if (!gcpManager) {
                return;
            }

            gcpManager.activate();
            assetViewer.showHideSceneBackground(true);

            const panel = window.panelManager?.getPanel("multi-gcp-editor") as
                | { initWithGCPManager: (m: typeof gcpManager) => void }
                | undefined;

            panel?.initWithGCPManager(gcpManager);
            window.panelManager?.openPanelSync("multi-gcp-editor", { parentPanelId: "transform" });
        });

        this._initGlobeDisplayStatusFormGroup();
    }

    /** Show transform container and globe status; hide editor views. Called by child panels on save/cancel. */
    showFirstPage(): void {
        this._jqTransformContainer?.show();
        this._jqGlobeStatusDisplayContainer?.show();
        this._jqVisualPositionEditorContainer?.removeClass("active");
        this._jqMultiGCPsEditorContainer?.removeClass("active");
    }

    /** Refresh geolocation labels and editor checkboxes. Called by child panels on save. */
    updateTransformPopup(): void {
        this._updateGeolocationLabels();
        this._updateEditorsCheckbox();
        this._setVisibilityGeolocation();
    }

    reset(): void {
        this.showFirstPage();
        this.updateTransformPopup();
    }

    private _updateGeolocationLabels(): void {
        const CONSTRUKTED_AJAX = window.CONSTRUKTED_AJAX;
        let assetGeoLocationData: AssetGeoLocation | undefined;

        if (
            CONSTRUKTED_AJAX.asset_geo_location &&
            CONSTRUKTED_AJAX.active_editor === ConstruktedTransformEditors.VisualPositionEditor
        ) {
            assetGeoLocationData = CONSTRUKTED_AJAX.asset_geo_location as AssetGeoLocation;
        }

        if (
            CONSTRUKTED_AJAX.asset_geo_location_by_gcps &&
            CONSTRUKTED_AJAX.active_editor === ConstruktedTransformEditors.MultiGCPsEditor
        ) {
            assetGeoLocationData = CONSTRUKTED_AJAX.asset_geo_location_by_gcps as AssetGeoLocation;
        }

        if (
            CONSTRUKTED_AJAX.asset_geo_location_by_pre &&
            CONSTRUKTED_AJAX.active_editor === ConstruktedTransformEditors.PredefinedGeoreference
        ) {
            assetGeoLocationData = CONSTRUKTED_AJAX.asset_geo_location_by_pre as AssetGeoLocation;
        }

        if (!assetGeoLocationData) {
            const masterAsset = window.Construkted.assetViewer?.masterAsset;

            assetGeoLocationData = masterAsset?.originallyGeoreferencedAtECEF
                ? masterAsset.originalGeolocation()
                : {
                      longitude: 0,
                      latitude: 0,
                      height: 0,
                      heading: 0,
                      pitch: 0,
                      roll: 0,
                      localGeoRefX: 0,
                      localGeoRefY: 0,
                      localGeoRefZ: 0,
                      scale: { x: 1, y: 1, z: 1 }
                  };
        }

        const data = assetGeoLocationData as { longitude: number; latitude: number; height: number };

        if (!Number.isNaN(data?.longitude) && !Number.isNaN(data?.latitude) && !Number.isNaN(data?.height)) {
            this._jqLatitudeLabel?.text(data.latitude.toFixed(8));
            this._jqLongitudeLabel?.text(data.longitude.toFixed(8));
            this._jqElevationLabel?.text(data.height.toFixed(3));
        }
    }

    private _updateEditorsCheckbox(): void {
        const activeEditor = window.CONSTRUKTED_AJAX.active_editor;

        this._jqPredefinedGeoreferenceCheckbox?.prop(
            "checked",
            activeEditor === ConstruktedTransformEditors.PredefinedGeoreference
        );
        this._jqVisualGeolocationEditorCheckbox?.prop(
            "checked",
            activeEditor === ConstruktedTransformEditors.VisualPositionEditor
        );
        this._jqMultiGCPLocationEditorCheckbox?.prop(
            "checked",
            activeEditor === ConstruktedTransformEditors.MultiGCPsEditor
        );
    }

    private _setVisibilityGeolocation(): void {
        const jqGeolocationContainer = window.jQuery("#geolocation-container");

        if (window.CONSTRUKTED_AJAX.active_editor === ConstruktedTransformEditors.VisualPositionEditor) {
            if (window.CONSTRUKTED_AJAX.terrain_imagery_enabled) {
                jqGeolocationContainer.show();
            } else {
                jqGeolocationContainer.hide();
            }
        } else {
            jqGeolocationContainer.show();
        }
    }

    private _initGlobeDisplayStatusFormGroup(): void {
        const hideAll = () => {
            this._jqSubmitToGlobeButton?.hide();
            this._jqRemoveFromGlobeButton?.hide();
            this._jqAssetNotGeoreferencedDescription?.hide();
            this._jqAssetGeoreferencedDescription?.hide();
            this._jqAssetSubmittedDescription?.hide();
            this._jqAssetApprovedDescription?.hide();
        };
        const CONSTRUKTED_AJAX = window.CONSTRUKTED_AJAX;

        this._jqSubmitToGlobeButton?.click(() => {
            submitAssetGlobeStatusDisplay(() => {
                CONSTRUKTED_AJAX.globe_display_status = GlobeDisplayStatus.Submitted;
                hideAll();
                this._jqAssetSubmittedDescription?.show();
                this._jqRemoveFromGlobeButton?.show();
            }, undefined);
        });

        this._jqRemoveFromGlobeButton?.click(() => {
            removeAssetFromGlobe(() => {
                CONSTRUKTED_AJAX.globe_display_status = GlobeDisplayStatus.Unknown;
                hideAll();

                if (CONSTRUKTED_AJAX.asset_geo_location) {
                    this._jqAssetGeoreferencedDescription?.show();
                    this._jqSubmitToGlobeButton?.show();
                } else {
                    this._jqAssetNotGeoreferencedDescription?.show();
                }
            }, undefined);
        });

        hideAll();

        const status = CONSTRUKTED_AJAX.globe_display_status;

        if (status === GlobeDisplayStatus.Unknown) {
            if (CONSTRUKTED_AJAX.asset_geo_location) {
                this._jqAssetGeoreferencedDescription?.show();
                this._jqSubmitToGlobeButton?.show();
            } else {
                this._jqAssetNotGeoreferencedDescription?.show();
            }
        } else if (status === GlobeDisplayStatus.Submitted) {
            this._jqAssetSubmittedDescription?.show();
            this._jqRemoveFromGlobeButton?.show();
        } else if (status === GlobeDisplayStatus.Approved) {
            this._jqAssetApprovedDescription?.show();
            this._jqRemoveFromGlobeButton?.show();
        }
    }
}
